import {
  ActionTitle,
  Box,
  DisplayBetween,
  SearchBox,
  SearchInput,
  ThemeItem,
} from "../../common-components/StyledComponents.jsx";
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { LiaTimesSolid } from "react-icons/lia";
import { useDispatch, useSelector } from "react-redux";
import { setIsActionActive } from "../../store/slices/appAlice.js";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/bars-filter.svg";
import ScrollLoader from "../../common-components/ScrollLoader.jsx";
import ConfirmationDialog from "../../components/popups/ConfirmationDialog.jsx";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll.js";
import {
  apiPost,
  apiGet,
} from "../../library/utils/common-services/apiCall.js";
import { ENDPOINTS } from "../../library/utils/constants/apiurl.js";
import {
  EDITOR_ASSETS,
  EDITOR_TYPES,
  USER_TYPES,
  ORIENTATION,
  isValidSubTypeForEditor,
  EDITOR_TYPE_SUB_TYPES,
} from "../../library/utils/constants/index.js";
import {
  getCanvasSize,
  getActiveEditorType,
  getOrientation,
  getSettings,
} from "../../library/utils/helpers/index.js";
import {
  setEditorType,
  setCurrentObjectProperties,
  applyTheme,
  setCalendarSettings,
  setCanvasSize,
  setSettings,
  replaceSettings,
} from "../../store/slices/canvas.js";
import {
  setEditorPages,
  setThemeId,
  setAllThemes,
  setThemeName,
} from "../../store/slices/projectSetup.js";
import {
  decompressFromBase64,
  scalePages,
  getMaskIds,
  replaceMaskIdWithPath,
} from "../../library/utils/common-functions/index.js";
import { GetThemeById, processProjectPages } from "../../library/utils/services/theme/index.js";
import Select from "react-dropdown-select";
import { PageLoader } from "../../common-components/Loaders.js";
import BlankImagePlaceholder from "../../assets/images/blankImagePlaceholder.png";

// Project/brand-scope flags that must NOT be wiped by a theme switch.
// These represent user permissions / product-level config and are set once
// at init (e.g. from brandDetails or user role), not per theme.
const PRESERVED_SETTINGS_KEYS = [
  "allowImageDelete",
  "maxImageUploadLimit",
  "applyMaximumImageUploadLimit",
  "allowBackgroundRemover",
];

let lastScrollTopThemes = 0;
let lastVisitedPageThemes = 1;

// Memoized theme grid item to prevent re-renders when sibling items change
const ThemeGridItem = React.memo(({ theme, onClick }) => {
  const imgUrl =
    (
      theme?.theme_images &&
      theme.theme_images.length > 0 &&
      theme.theme_images[0]?.url
    ) ?
      theme.theme_images[0].url
    : BlankImagePlaceholder;
  return (
    <div className="col-6 mb-4 d-flex justify-content-center">
      <ThemeItem
        src={imgUrl}
        alt={theme.name}
        title={theme.name}
        className="img-fluid theme-item"
        loading="lazy"
        onClick={onClick}
      />
    </div>
  );
});
export const ThemesAction = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [themeCategories, setThemeCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const canvasSize = useSelector(getCanvasSize);
  const activeEditorType = useSelector(getActiveEditorType);
  const activeOrientation = useSelector(getOrientation);
  const currentSettings = useSelector(getSettings);
  const cartDetails = useSelector((state) => state.projectSetup.cartDetails);

  // Keep refs up-to-date so fetchThemes can read latest values without
  // changing its identity when user/cartDetails load async from redux/localStorage.
  const userRef = useRef(user);
  const cartDetailsRef = useRef(cartDetails);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    cartDetailsRef.current = cartDetails;
  }, [cartDetails]);

  const [isUserInitialized, setIsUserInitialized] = useState(false);

  // ── Subtype filter ──────────────────────────────────────────────────────────
  // The subtype this product actually is. Guarded because the canvas slice
  // initialises settings.subtype to "wall_calendar" for EVERY editor type, so the
  // raw value can't be trusted — filtering a photobook by it would return nothing.
  const projectSubtype = isValidSubTypeForEditor(
    activeEditorType,
    currentSettings?.subtype,
  )
    ? currentSettings.subtype
    : null;

  // Admin-only override. `undefined` = follow the project (the customer path, and
  // the admin's initial state); `null` = "All subtypes"; a string = explicit pick.
  // Kept as undefined-vs-null rather than syncing state in an effect, because
  // currentSettings loads async and an effect would clobber an admin's choice.
  const [subtypeOverride, setSubtypeOverride] = useState(undefined);

  const isAdminLike =
    user?.userTypeCode === USER_TYPES.SUPERUSER ||
    user?.userTypeCode === USER_TYPES.ADMIN ||
    user?.userTypeCode === USER_TYPES.EMPLOYEE;

  // Customers never get the control, so their override stays undefined and the
  // list is always pinned to the product they actually bought.
  const effectiveSubtype =
    isAdminLike && subtypeOverride !== undefined ? subtypeOverride : projectSubtype;

  const subtypeOptions = Object.values(
    EDITOR_TYPE_SUB_TYPES[activeEditorType] || {},
  );
  const showSubtypeFilter = isAdminLike && subtypeOptions.length > 0;

  const themesPerPage = 20;

  const initUser = () => {
    if (user != null) {
      setIsUserInitialized(true);
      return;
    }
    const users = localStorage.getItem("userDetails");
    if (!users) {
      setUser(null);
      setIsUserInitialized(true);
      return;
    }
    const userObj = JSON.parse(users);
    setUser(userObj);
    setIsUserInitialized(true);
  };

  useEffect(() => {
    initUser();

    const data = {
      filter: {
        status: 1,
        sample: true,
        type: EDITOR_ASSETS.THEME,
      },
    };

    apiPost(ENDPOINTS.getThemesCategory, data)
      .then((response) => {
        if (
          response &&
          response.status === 1 &&
          response.items &&
          response.items.length > 0
        ) {
          setThemeCategories(response.items);
        }
      })
      .catch((error) => {
        console.error("Error fetching themes categories:", error);
      });
  }, []);

  // Wrap API call for the hook
  const fetchThemes = useCallback(
    async (page, skip, limit) => {
      // Read via refs so this function's identity stays stable while
      // user/cartDetails load asynchronously (avoids spurious page-1 resets).
      const u = userRef.current;
      const cd = cartDetailsRef.current;
      const data = {
        filter: {
          status:
            (
              u != null &&
              (u?.userTypeCode === USER_TYPES.SUPERUSER ||
                u?.userTypeCode === USER_TYPES.ADMIN ||
                u?.userTypeCode === USER_TYPES.EMPLOYEE)
            ) ?
              { $in: [1, 3] }
            : 1,
          tagId: selectedCategoryIds.length > 0 ? selectedCategoryIds : null,
          editor_type: activeEditorType,
          display_in_web: true,
          search: searchText,
          brand_id: u?.brand_id ? u.brand_id : null,
          userTypeCode: u?.userTypeCode ? u?.userTypeCode : null,
          product_id: cd?.product_id ? cd.product_id : null,
          // Only list themes built for this product's subtype (e.g. a wall calendar
          // must not show wooden/mountain calendar themes). For customers this is
          // always the product's own subtype; an admin can widen it via the Subtype
          // dropdown. null => backend applies no subtype filter.
          subtype: effectiveSubtype,
        },
        skip: skip ?? (page - 1) * themesPerPage,
        limit: limit ?? themesPerPage,
        sort: { createdAt: -1 },
      };

      const response = await apiPost(ENDPOINTS.getThemes, data);
      if (response && response.items) {
        // Strip heavy data from items
        response.items.forEach((item) => {
          delete item.pages_c;
          delete item.theme;
        });
      }
      return {
        items: response?.items || [],
        totalCount: response?.totalCount || 0,
      };
    },
    // effectiveSubtype is a dep (not a ref like user/cartDetails): changing it
    // genuinely changes the result set, so the list must refetch. It's a primitive,
    // so an unrelated settings change won't trigger a refetch.
    [searchText, selectedCategoryIds, activeEditorType, effectiveSubtype], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const {
    items: themes,
    loading,
    isFetchingMore,
    hasMore,
    sentinelRef,
    scrollContainerRef,
    resetAndFetch,
    currentPageRef: themesCurrentPageRef,
  } = useInfiniteScroll({
    fetchFn: fetchThemes,
    itemsPerPage: themesPerPage,
    enabled: isUserInitialized,
    direction: "vertical",
    restoreToPage: lastVisitedPageThemes,
    cacheKey: "themes",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: () => { },
    showCancel: true,
    confirmText: "Yes",
  });

  const handleClick = (theme) => {
    setConfirmDialog({
      show: true,
      title: "Apply Theme",
      message: "Do you want to apply this theme?",
      showCancel: true,
      confirmText: "Yes",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
        const themeData = await GetThemeById(
          theme._id,
          canvasSize,
          activeEditorType,
          activeOrientation,
        );

        if (!themeData) {
          setConfirmDialog({
            show: true,
            title: "Theme Unavailable",
            message: "The selected theme is not available for the current product size. Please try another theme.",
            showCancel: false,
            confirmText: "OK",
            onConfirm: () => setConfirmDialog((prev) => ({ ...prev, show: false })),
          });
          return;
        }

        setupTheme(themeData, theme._id);
      },
    });
  };

  const setupTheme = (themeData, themeid) => {
    if (!themeData && !themeData.pages_c) {
      return;
    }
    dispatch(setCalendarSettings(null));

    // Determine target canvas dims + pick the raw-base64 variant whose pages
    // must be applied. For admin: prefer variant matching current canvas size,
    // else theme[0]. For customer: match orientation or fall back to theme[0].
    const isAdminLike =
      user != null &&
      (user?.userTypeCode === USER_TYPES.SUPERUSER ||
        user?.userTypeCode === USER_TYPES.ADMIN ||
        user?.userTypeCode === USER_TYPES.EMPLOYEE);

    let targetWidth = canvasSize.width;
    let targetHeight = canvasSize.height;
    let selectedVariant = null;

    if (isAdminLike) {
      const sizeToCheck = `${canvasSize.width}x${canvasSize.height}`;
      selectedVariant =
        themeData.theme.find((item) => item.size === sizeToCheck) ||
        themeData.theme[0];
      if (selectedVariant) {
        targetWidth = parseFloat(selectedVariant.width);
        targetHeight = parseFloat(selectedVariant.height);
        // Use NEW theme's margins only — do NOT fall back to previous canvasSize
        // margins. Previously `|| canvasSize?.safeMargin` caused margins from the
        // OLD theme to persist when the new theme declares 0/undefined.
        dispatch(
          setCanvasSize({
            ...canvasSize,
            width: targetWidth,
            height: targetHeight,
            depth: parseFloat(selectedVariant.depth) || 0,
            safeMargin: parseFloat(selectedVariant.safe_margin) || 0,
            bleedMargin: parseFloat(selectedVariant.bleed_margin) || 0,
          }),
        );
      }
    } else {
      // Customer: keep canvas size, pick orientation-matching variant.
      selectedVariant =
        themeData.theme.find((t) => t.orientation === activeOrientation) ||
        themeData.theme[0];
    }

    // Re-scale pages by decompressing the chosen variant's RAW base64 directly
    // to the target size. GetThemeById already produced a decoded themeData.pages_c,
    // but that was scaled for a potentially different variant (theme[0] by default)
    // and chaining scalePages on top introduces non-commutative errors in
    // `obj.image.scale *= Math.max(widthScale, heightScale)` — the image crop shifts.
    // processProjectPages mirrors SizeSettingsPopup's path (single scale from raw),
    // guaranteeing identical object positions + image zoom as "resize to same size".
    let scaledPagesForDispatch = themeData.pages_c;
    if (
      selectedVariant?.pages_c &&
      typeof selectedVariant.pages_c === "string" &&
      targetWidth &&
      targetHeight
    ) {
      const layoutWidth =
        themeData.editor_type === EDITOR_TYPES.PHOTOBOOK
          ? targetWidth / 2
          : targetWidth;
      scaledPagesForDispatch = processProjectPages(
        selectedVariant.pages_c,
        layoutWidth,
        targetHeight,
      );
    }

    if (themeData.theme[0].cal_settings) {
      let calSettings = { ...themeData.theme[0].cal_settings };
      if (themeData.theme[0].cal_settings.startMonth) {
        calSettings.startMonth = null;
      }
      if (themeData.theme[0].cal_settings.startYear) {
        calSettings.startYear = null;
      }

      dispatch(setCalendarSettings(calSettings));
    }
    // Full-replace settings so OLD theme's spineWidth / coverEnabled /
    // showFullCoverSheet / paperThickness / hideLastCover / isFoldable don't
    // leak into the new theme (which may not declare them). setSettings merges
    // with existing state.settings — use replaceSettings here instead.
    // Carry over project/brand-scope flags (user rights, upload limits) that
    // are NOT theme-controlled so a theme switch doesn't silently downgrade them.
    const nextThemeSettings = themeData.theme[0].settings || {};
    const preserved = {};
    PRESERVED_SETTINGS_KEYS.forEach((key) => {
      if (currentSettings?.[key] !== undefined && nextThemeSettings[key] === undefined) {
        preserved[key] = currentSettings[key];
      }
    });
    dispatch(replaceSettings({ ...preserved, ...nextThemeSettings }));

    // Defer page/theme dispatches so the new canvasSize commits first and
    // Canvas.jsx's 500ms canvasSize-change debounce (src/components/canvas/Canvas.jsx:1603)
    // runs updateCanvasSize before pages apply. 600ms = 500ms debounce + 100ms cushion.
    // Reduced from 1000ms (400ms saved per theme switch) now that pages are also
    // freshly rescaled via processProjectPages so viewport + pages stay consistent.
    setTimeout(() => {
      dispatch(setCurrentObjectProperties(null));
      dispatch(setEditorType(themeData.editor_type));
      dispatch(setEditorPages(scaledPagesForDispatch));
      dispatch(setThemeId(themeid));
      dispatch(setThemeName(themeData.name));
      if (
        user != null &&
        (user?.userTypeCode === USER_TYPES.SUPERUSER ||
          user?.userTypeCode === USER_TYPES.ADMIN ||
          user?.userTypeCode === USER_TYPES.EMPLOYEE)
      ) {
        dispatch(setAllThemes(themeData.theme));
      }
    }, 600);
  };

  const handleDropdownChange = (selectedOptions) => {
    const selectedIds = selectedOptions.map((option) => option._id);
    setSelectedCategoryIds(selectedIds);
    // resetAndFetch will be triggered via fetchThemes dependency change
  };

  // Scroll preservation
  const needsScrollRestoreThemes = useRef(lastScrollTopThemes > 0);
  const isFirstMountThemes = useRef(true);

  // Continuously save scroll position on every scroll event
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handler = () => {
      lastScrollTopThemes = el.scrollTop;
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  });

  // Restore scroll position after themes load on mount
  // Using useLayoutEffect to apply scroll BEFORE paint to prevent blink
  useLayoutEffect(() => {
    if (!needsScrollRestoreThemes.current) return;
    if (themes.length === 0 && !isFirstMountThemes.current) return;
    
    // Apply scroll synchronously before browser paint
    if (scrollContainerRef.current && lastScrollTopThemes > 0) {
      scrollContainerRef.current.scrollTop = lastScrollTopThemes;
    }
    needsScrollRestoreThemes.current = false;
    isFirstMountThemes.current = false;
  }, [themes.length]);

  // Save visited page on unmount so it can be restored on next mount
  useEffect(() => {
    return () => {
      lastVisitedPageThemes = themesCurrentPageRef.current;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        className="sticker-container sticker-container-mob"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          margin: "2px",
        }}
      >
        <DisplayBetween
          className="heading-action-mob"
          style={{ flexShrink: 0, borderBottom: "1px solid #f0f0f0" }}
        >
          <ActionTitle>Themes</ActionTitle>
          <LiaTimesSolid
            onClick={() => dispatch(setIsActionActive(false))}
            className="cursor-pointer"
          />
        </DisplayBetween>
        <div
          ref={scrollContainerRef}
          className="scroll-container-mob"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            paddingBottom: "20px",
            padding: "3px",
          }}
        >
          <Box mt="15px">
            <SearchBox>
              <Box
                className="search-icon"
                onClick={() => {
                  resetAndFetch();
                }}
              >
                <SearchIcon />
              </Box>
              <SearchInput
                type="text"
                placeholder="Search theme"
                onChange={(e) => {
                  e.preventDefault();
                  const value = e.target.value;
                  setSearchText(value);
                  if (!value.trim()) {
                    resetAndFetch();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    resetAndFetch();
                  }
                }}
              />
              <Box className="filter-icon">
                <FilterIcon />
              </Box>
            </SearchBox>
          </Box>

          {/* Admin-only. Customers get no control — their list stays pinned to the
              subtype of the product they actually bought. Hidden entirely for
              editor types that have no subtypes (canvas, print, wallart, …). */}
          {showSubtypeFilter && (
            <Box mt="15px">
              <select
                value={effectiveSubtype ?? ""}
                onChange={(e) =>
                  setSubtypeOverride(e.target.value === "" ? null : e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: "13px",
                  color: "#333",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                  cursor: "pointer",
                }}
                aria-label="Filter themes by subtype"
              >
                {/* Clears the filter. Needed to reach themes with a missing or
                    mis-tagged subtype, which do exist in the data. */}
                <option value="">All subtypes</option>
                {subtypeOptions.map((value) => (
                  <option key={value} value={value}>
                    {value
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (ch) => ch.toUpperCase())}
                  </option>
                ))}
              </select>
            </Box>
          )}

          {themeCategories.length > 0 && (
            <Box mt="15px">
              <Select
                multi
                options={themeCategories.filter((item) =>
                  item.name.toLowerCase(),
                )}
                labelField="name"
                valueField="_id"
                placeholder="Select Categories"
                onChange={handleDropdownChange}
                searchBy="name"
                searchable
                clearable
                style={{ width: "100%", marginBottom: "20px" }}
              />
            </Box>
          )}

          {loading && <PageLoader />}
          <div className="mt-3">
            <div className="row">
              {themes.map((theme) => (
                <ThemeGridItem
                  key={theme._id}
                  theme={theme}
                  onClick={() => handleClick(theme)}
                />
              ))}
            </div>

            {/* Sentinel + loader for infinite scroll */}
            <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />
            {isFetchingMore && <ScrollLoader />}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, show: false }))}
        showCancelButton={confirmDialog.showCancel}
        confirmText={confirmDialog.confirmText}
      />
    </>
  );
};
